// THE SHARED CENTERED-MODAL SHELL (slice ui-redesign, increment 1).
//
// ⚠️ HAND-ROLLED, NOT SHADCN, despite living in components/ui/. Everything
// else in this folder is a Radix-based shadcn primitive that the app's own
// screens do not use (verified: zero imports of ui/dialog outside this
// folder). This file is deliberately in the app's own inline-style idiom,
// sourcing every value from styles/tokens.ts, because that is what the four
// modals it is meant to absorb already are. Adopting Radix's Dialog instead
// would have been a behaviour change, not a restyle.
//
// WHAT IT OWNS, and why each piece is here rather than in the caller:
//
//   The close contract.  requestClose is the single gate. `busy` blocks ALL
//                        THREE close routes at once — backdrop, Escape and
//                        the X. Before this shell, NewProductModal and
//                        ImportCsvModal each re-derived that rule and the
//                        other three modals did not have it at all, so a
//                        MaterialPicker mid-request could be dismissed.
//   Escape.              Listener on `window`, not on the panel, so it fires
//                        no matter where focus is — including nowhere, which
//                        is the state a freshly-opened modal is in before
//                        anything is focused.
//   Focus.               Moves focus INTO the panel on mount, traps Tab
//                        inside it, and returns focus to whatever opened the
//                        modal on unmount. None of the five modals did any
//                        of this; Tab used to walk straight out of the modal
//                        into the page behind the scrim.
//   Labelling.           role="dialog" + aria-modal + aria-labelledby wired
//                        to the real title node, so the modal announces
//                        itself instead of being an anonymous div.
//
// WHAT IT DOES NOT OWN. The caller keeps its own state, its own submit, its
// own validation, and its own footer buttons (each needs its own testids and
// its own disabled rule). This shell is chrome and close-behaviour only — it
// must never learn what a product is.
//
// TESTIDS. Derived from `testIdPrefix` so an adopting modal keeps the exact
// four it already published: `${prefix}-backdrop`, `${prefix}-modal`,
// `${prefix}-title`, `${prefix}-close`. For NewProductModal the prefix is
// "new-product", which reproduces new-product-backdrop / -modal / -title /
// -close byte for byte. Do not "tidy" this into a different scheme: those
// four strings are a published contract that tests and QA tooling match on.

import { useCallback, useEffect, useId, useRef } from "react";
import { X } from "lucide-react";
import {
  semanticColors, radius, spacing, heights, layout, typography, iconSize, iconStroke, shadows,
} from "@/styles/tokens";

/** Everything focusable we care about trapping Tab within. */
const FOCUSABLE = [
  "a[href]", "button:not([disabled])", "input:not([disabled])",
  "select:not([disabled])", "textarea:not([disabled])", "[tabindex]:not([tabindex='-1'])",
].join(",");

export interface ModalProps {
  /** Drives the four chrome testids. See the header note — this is a contract. */
  testIdPrefix: string;
  title: React.ReactNode;
  /** Optional line beneath the title. */
  subtitle?: React.ReactNode;
  onClose: () => void;
  /**
   * ⚠️ Blocks every close route while true. Pass the caller's in-flight flag.
   * A modal whose request is mid-flight must not be dismissable: the POST may
   * well have committed, and nobody would ever see the result.
   */
  busy?: boolean;
  /** Panel width. Defaults to the app's standard modal width. */
  width?: number | string;
  children: React.ReactNode;
  /** The button row. Owned by the caller — each modal's buttons need their own testids. */
  footer?: React.ReactNode;
}

export function Modal({
  testIdPrefix, title, subtitle, onClose, busy = false,
  width = layout.modalWidth, children, footer,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Captured on mount, restored on unmount. Without it, dismissing the modal
  // drops focus onto <body> and a keyboard user restarts from the top of the
  // page rather than from the control they opened it with.
  const openerRef = useRef<Element | null>(null);
  const titleId = `${useId()}-title`;

  // The ONE close gate. Backdrop, Escape and the X all route through here, so
  // `busy` cannot be honoured by two of the three and forgotten by the last.
  const requestClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  // ⚠️ On `window`, matching what every modal already did. A listener on the
  // panel would only fire once something inside it had focus.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [requestClose]);

  useEffect(() => {
    openerRef.current = document.activeElement;
    // The panel itself, not the first field: focusing a text input on open
    // pops the software keyboard on a touch device and skips the title for a
    // screen reader, which is the first thing that should be announced.
    panelRef.current?.focus();
    return () => {
      const opener = openerRef.current;
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
  }, []);

  // Tab trap. Only Tab is intercepted — Escape is handled above, and every
  // other key must reach the fields untouched.
  const onPanelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE))
      .filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      data-testid={`${testIdPrefix}-backdrop`}
      onClick={requestClose}
      style={{
        position: "fixed", inset: 0, backgroundColor: semanticColors.overlay,
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        padding: spacing[16], // keeps the panel off the edges on a short/narrow viewport
      }}
    >
      <div
        ref={panelRef}
        data-testid={`${testIdPrefix}-modal`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onPanelKeyDown}
        style={{
          width, maxWidth: "100%", maxHeight: "90vh",
          backgroundColor: semanticColors.panelBg,
          border: `1px solid ${semanticColors.border}`,
          borderRadius: radius.xl,
          boxShadow: shadows.modal,
          display: "flex", flexDirection: "column",
          outline: "none", // the panel is a focus HOLDER, not a focus target
        }}
      >
        {/* Header. No divider rule: the redesign separates it by whitespace
            instead, so the panel reads as one sheet rather than three bands. */}
        <div
          className="flex items-start justify-between"
          style={{ padding: `${spacing[24]}px ${spacing[24]}px ${spacing[8]}px`, gap: spacing[16] }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              id={titleId}
              data-testid={`${testIdPrefix}-title`}
              style={{
                fontSize: typography.fontSize.xxxl,
                fontWeight: typography.fontWeight.semibold,
                color: semanticColors.textPrimary,
                letterSpacing: typography.letterSpacing.tight,
                lineHeight: 1.2,
              }}
            >
              {title}
            </div>
            {subtitle && (
              <div style={{
                marginTop: spacing[4], fontSize: typography.fontSize.base,
                color: semanticColors.textSecondary,
              }}>
                {subtitle}
              </div>
            )}
          </div>
          <button
            type="button"
            data-testid={`${testIdPrefix}-close`}
            onClick={requestClose}
            disabled={busy}
            title="Close"
            aria-label="Close"
            style={{
              // 32px, not the 24px icon box the old header used: an icon-only
              // control needs a real hit area, and this is the only dismiss
              // affordance a pointer user is guaranteed to find.
              width: heights.control, height: heights.control, flexShrink: 0,
              padding: 0, borderRadius: radius.lg,
              cursor: busy ? "not-allowed" : "pointer",
              background: "transparent", border: "none",
              color: busy ? semanticColors.textDisabled : semanticColors.textSecondary,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={iconSize.lg} strokeWidth={iconStroke.regular} />
          </button>
        </div>

        {/* Body */}
        <div style={{
          flex: 1, minHeight: 0, overflowY: "auto",
          padding: `${spacing[8]}px ${spacing[24]}px`,
        }}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div style={{
            padding: `${spacing[16]}px ${spacing[24]}px ${spacing[24]}px`,
            display: "flex", gap: spacing[8], justifyContent: "flex-end", alignItems: "center",
            flexWrap: "wrap",
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
